import { TreeNodeData, useTree, Tree, getTreeExpandedState } from "@mantine/core";
import useStore from "../../data/store";
import useTransientStore from "../../data/transcientStore";
import { useState, useEffect, useMemo, useCallback } from "react";
import LocationLeaf from "./locationLeaf";
import { useTranslation } from "../../hooks/useTranslation";

interface LocationTreeProps {
	data: TreeNodeData[];
	updateTreeData: (data: TreeNodeData[]) => void;
	setCurrentNode: (node: TreeNodeData) => void;
	setFormOpen: (open: boolean) => void;
	// Signal integer increments to force full expand (e.g., Show All Layers action)
	expandAllSignal?: number;
}

export default function LocationTree ({ data, updateTreeData, setCurrentNode, setFormOpen, expandAllSignal }: Readonly<LocationTreeProps>) {
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({ pageId: 341, addressSystemId: addrIdFromStore || 0, autoLoad: true });
    // Memoize the initial expanded state to avoid recalculation
	const initialExpandedState = useMemo(() => getTreeExpandedState(data, '*'), []);
	
	const userGeometriesVisible = useTransientStore((state) => state.userGeometriesVisible);
	const setUserGeometriesVisible = useTransientStore((state) => state.setUserGeometriesVisible);
	
	const tree = useTree({
		initialExpandedState,
		initialCheckedState: userGeometriesVisible ? ['rims-preset-node-user-geometries'] : []
	});
	
    const clickedPolygonIDs = useStore((state) => state.clickedPolygonIDs);
	const setClickedPolygonIDs = useStore((state) => state.setClickedPolygonIDs);
    const searchFilter = useTransientStore((state) => state.searchFilter);

    const [localTreeData, setLocalTreeData] = useState<TreeNodeData[]>([]);

    // Function to recursively filter tree nodes based on search term
    const filterTreeNodes = useCallback((nodes: TreeNodeData[], searchTerm: string): TreeNodeData[] => {
        if (!searchTerm.trim()) return nodes;

        const filteredNodes: TreeNodeData[] = [];

        for (const node of nodes) {
            // Check if current node matches search term
            const nodeLabel = typeof node.label === 'string' ? node.label : String(node.label);
            const nodeMatches = nodeLabel.toLowerCase().includes(searchTerm.toLowerCase());
            
            // Recursively filter children
            const filteredChildren = node.children ? filterTreeNodes(node.children, searchTerm) : [];
            
            // Include node if:
            // 1. The node itself matches the search term, OR
            // 2. Any of its children match (filteredChildren.length > 0)
            if (nodeMatches || filteredChildren.length > 0) {
                filteredNodes.push({
                    ...node,
                    children: filteredChildren.length > 0 ? filteredChildren : node.children
                });
            }
        }

        return filteredNodes;
    }, []);

    // Apply search filter to the tree data - avoid deep clone when not filtering
    const filteredTreeData = useMemo(() => {
        if (!searchFilter.trim()) {
            return data;
        }
        // Only clone when we need to filter
        const baseData = JSON.parse(JSON.stringify(data)) as TreeNodeData[];
        return filterTreeNodes(baseData, searchFilter);
    }, [data, searchFilter, filterTreeNodes]);

	useEffect(() => {
		setLocalTreeData(filteredTreeData);
		const shouldAutoExpand = searchFilter.trim() || typeof expandAllSignal === 'number';
		if (shouldAutoExpand) {
			setTimeout(() => {
				const expandAllNodes = (nodes: TreeNodeData[]) => {
					nodes.forEach(node => {
						if (node.children && node.children.length > 0) {
							tree.expand(node.value);
							expandAllNodes(node.children);
						}
					});
				};
				expandAllNodes(filteredTreeData);
			}, 50);
		}
	}, [filteredTreeData, searchFilter, tree, expandAllSignal]);

    // Function to recursively expand nodes up to the target node
	function expandNodeById(nodes: TreeNodeData[], id: string): boolean {
		for (const node of nodes) {
			// If the node has children, we recursively check them
			if (node.children) {
				const found = expandNodeById(node.children, id);
				if (found) {
					tree.expand(node.value); // Expand this parent node
					return true; // Node found, no need to search further
				}
			}

			// Check if the current node is the target node
			if (node.nodeProps?.object?.LocalID === id) {
				tree.expand(node.value); // Expand the node
				return true; // Node found
			} else {
				//Collapse the node if it is not the target node
				tree.collapse(node.value);
			}
		}

		return false; // Node not found in this branch
	}

    /**
	 * Recursively sets visibility for nodes. When the target node is an area, all its subitems remain visible.
	 * @param nodes - Array of tree nodes.
	 * @param targetNode - The node that should remain visible.
	 * @returns The updated array of tree nodes.
	 */
	function hideAllExceptTarget(nodes: TreeNodeData[], targetNode: TreeNodeData): TreeNodeData[] {
		return nodes.map((nodeValue): TreeNodeData => {
			if (nodeValue.value === targetNode.value) {
				nodeValue.nodeProps = { ...nodeValue.nodeProps, visible: true };
				// Ensure children (subitems) remain visible if the target is an area
				if (nodeValue.children) {
					nodeValue.children = nodeValue.children.map((child) => ({
						...child,
						nodeProps: { ...child.nodeProps, visible: true }
					}));
				}
			} else {
				nodeValue.nodeProps = { ...nodeValue.nodeProps, visible: false };
				if (nodeValue.children) {
					nodeValue.children = hideAllExceptTarget(nodeValue.children, targetNode);
				}
			}
			return nodeValue;
		});
	}

	/**
	 * Updates the tree data and collapses the nodes accordingly.
	 * @param nodeValue - The value of the node to be updated.
	 */
	const onUpdate = useCallback((nodeValue: TreeNodeData) => {
		const updatedTreeData = hideAllExceptTarget(localTreeData, nodeValue);
		setLocalTreeData(updatedTreeData);
		updateTreeData(updatedTreeData);
		tree.collapse(nodeValue.value);
	}, [localTreeData, updateTreeData, tree]);

	const handleOpenModal = useCallback((node: TreeNodeData) => {
		setCurrentNode(node);
		if (node.nodeProps?.object?.LocalID) {
			setClickedPolygonIDs([node.nodeProps.object.LocalID]);
		}
		setFormOpen(true);
	}, [setCurrentNode, setClickedPolygonIDs, setFormOpen]);

	const renderNode = useCallback((payload: any) => (
		<LocationLeaf {...payload} onUpdate={onUpdate} openModal={handleOpenModal} />
	), [onUpdate, handleOpenModal]);

	return (
        <Tree
			tree={tree}
            data={localTreeData}
            levelOffset={23}
			expandOnClick={false}
            renderNode={renderNode}
        />
    );
    
}